import { ACTION_TYPE_NAME } from "../../../../components/dashboard/flowBuilder/flowBuilderConstants";
import { 
    currentActiveChatbotThreadTypeNew,
    currentActiveThreadChatbotThreadData,
    getTemplateDetails,
    sendCannedResponsePayload,
    sendTemplatePayload,
    templateVariable,
    uploadedMedia,
    UserMeResponseDataModel,
    WA_carousel_template_action_execution_data,
    WA_template_action_execution_data,
    WA_template_header_variable_data
} from "../../../../models/axiosModel";
import { useRuleBasedInboxStore } from "../../../../store/ruleBasedInboxStore";
import { scrollToBottomInChat, sortThreadMessageByTimeStamp } from "../../utils";
    // WA_template_action_execution_data,
    // WA_template_header_variable_data,
// import { useRuleBasedInboxStore } from '../../../store/ruleBasedInboxStore';

const buildSingleOptimisticManualMessage = ({
    flagMediaMessage,
    inputValue,
    media,
    userDetail,
    pendingMessageKey,
    timestamp,
    stepTimestamp,
}: {
    flagMediaMessage: boolean;
    inputValue: string;
    media?: uploadedMedia;
    userDetail: UserMeResponseDataModel;
    pendingMessageKey: string;
    timestamp: string;
    stepTimestamp: string;
}): currentActiveThreadChatbotThreadData => {
    const actionTypeName = flagMediaMessage ? ACTION_TYPE_NAME.SEND_MEDIA : ACTION_TYPE_NAME.TEXT;
    const messageText = flagMediaMessage
        ? (media?.caption ?? '')
        : inputValue;

    const actionExecutionData = flagMediaMessage
        ? {
            message: messageText,
            message_type: 'media',
            whatsapp_message_status: [],
            media,
            caption: media?.caption,
            file_name: media?.file_name,
        }
        : {
            message: inputValue,
            message_type: 'text',
            whatsapp_message_status: [],
        };

    return {
        thread_execution_log_id: null as unknown as string,
        chatbot_thread_id: null as unknown as string,
        chatbot_thread_value: messageText,
        timestamp,
        step_end_timestamp: stepTimestamp,
        step_start_timestamp: stepTimestamp,
        flag_user_message: false,
        flag_show_action: true,
        isPending: true,
        pendingMessageKey,
        action_type_name: actionTypeName,
        chatbot_thread_action_type: actionTypeName,
        chatbot_route_source: '',
        chatbot_user_active_flag: true,
        user_assignment_id: userDetail?.user_assignment_id ?? null,
        thread_chatbot_id: null,
        user_first_name: userDetail?.user_first_name ?? '',
        user_last_name: userDetail?.user_last_name ?? '',
        action_type_id: '',
        chatbot_step_action_id: null,
        execution_status_id: '',
        execution_status_name: '',
        group_id: null,
        parent_id: null,
        parent_thread_execution_log_id: null,
        chatbot_data: { chatbot_id: '', chatbot_name: '', chatbot_version_id: '' },
        action_execution_data: actionExecutionData as unknown as currentActiveThreadChatbotThreadData['action_execution_data'],
        chatbot_action_execution_data: {
            ...(actionExecutionData as object),
            private_user_first_name: userDetail?.user_first_name ?? null,
            private_user_last_name: userDetail?.user_last_name ?? null,
            user_assignment_id: userDetail?.user_assignment_id,
            whatsapp_message_status: [],
        } as currentActiveThreadChatbotThreadData['chatbot_action_execution_data'],
    } as currentActiveThreadChatbotThreadData;
};

export const buildOptimisticManualMessage = ({
    flagMediaMessage,
    inputValue,
    selectedMediaSet,
    userDetail,
    pendingMessageKeys,
    timestamp,
    stepTimestamp,
}: {
    flagMediaMessage: boolean;
    inputValue: string;
    selectedMediaSet: uploadedMedia[];
    userDetail: UserMeResponseDataModel;
    pendingMessageKeys: string[];
    timestamp: string;
    stepTimestamp: number;
}): currentActiveThreadChatbotThreadData[] => {
    if (flagMediaMessage) {
        return (selectedMediaSet || []).map((media, index) =>
            buildSingleOptimisticManualMessage({
                flagMediaMessage: true,
                inputValue,
                media,
                userDetail,
                pendingMessageKey: pendingMessageKeys[index],
                timestamp,
                // Keep media order stable when timestamps collide during sort
                stepTimestamp: String(stepTimestamp + index),
            }),
        );
    }

    return [
        buildSingleOptimisticManualMessage({
            flagMediaMessage: false,
            inputValue,
            userDetail,
            pendingMessageKey: pendingMessageKeys[0],
            timestamp,
            stepTimestamp: String(stepTimestamp),
        }),
    ];
};

export const buildOptimisticCannedResponseMessages = ({
    cannedResponsePayload,
    userDetail,
    pendingMessageKeys,
    timestamp,
    stepTimestamp,
}: {
    cannedResponsePayload: sendCannedResponsePayload;
    userDetail: UserMeResponseDataModel;
    pendingMessageKeys: string[];
    timestamp: string;
    stepTimestamp: number;
}): currentActiveThreadChatbotThreadData[] => {
    return (cannedResponsePayload?.canned_response_messages || []).map((msg, index) => {
        const isMediaMessage = msg?.message_type?.toLowerCase() === 'media';
        const mediaItem = msg?.media?.[0];

        return buildSingleOptimisticManualMessage({
            flagMediaMessage: isMediaMessage,
            inputValue: msg?.text ?? '',
            media: mediaItem
                ? {
                    type: mediaItem.type,
                    url: mediaItem.url,
                    caption: mediaItem.caption,
                    file_name: '',
                }
                : undefined,
            userDetail,
            pendingMessageKey: pendingMessageKeys[index],
            timestamp,
            stepTimestamp: String(stepTimestamp + index),
        });
    });
};

/** Extracts thread_execution_log_id from sendWhatsAppTemplate response data */
export const getTemplateMessageId = (responseData: unknown): string | null => {
    if (!responseData || typeof responseData !== 'object') return null;
    const messageId = (responseData as { thread_execution_log_id?: string })?.thread_execution_log_id;
    return typeof messageId === 'string' && messageId ? messageId : null;
};

/** Extracts thread_execution_log_id values from sendCannedResponse response data: [[changeLogId, uuid], ...] */
export const getCannedResponseMessageIds = (responseData: unknown): string[] => {
    if (!Array.isArray(responseData)) return [];

    return responseData
        .map((entry) => {
            if (Array.isArray(entry) && typeof entry[1] === 'string') {
                return entry[1];
            }
            return null;
        })
        .filter((id): id is string => Boolean(id));
};

const getTemplateVariableValue = (
    variableId: number | string | null | undefined,
    templateVariableData: templateVariable[],
): string => {
    if (variableId == null) return '';
    const matched = templateVariableData?.find((variable) => variable?.variable_id == variableId);
    return matched?.variable_value?.trim() || '';
};

const replaceTemplatePlaceholders = (
    templateText: string | null | undefined,
    variableData: { whatsapp_template_dynamic_variable_id?: number | null, whatsapp_template_dynamic_variable_key?: string | null }[] | null | undefined,
    templateVariableData: templateVariable[],
): string | null => {
    if (!templateText) return templateText ?? null;

    let result = templateText;
    variableData?.forEach((variable) => {
        const placeholder = variable?.whatsapp_template_dynamic_variable_key;
        if (!placeholder) return;
        const value = getTemplateVariableValue(variable?.whatsapp_template_dynamic_variable_id, templateVariableData);
        if (value) {
            result = result.replaceAll(placeholder, value);
        }
    });
    return result;
};

/** Builds ChatLogsTemplatePreview-compatible action_execution_data for Basic templates only. */
export const buildBasicTemplateActionExecutionData = (
    messageTemplateDetail: getTemplateDetails | null,
    templateVariableData: templateVariable[],
): NonNullable<sendTemplatePayload['action_execution_data']> | null => {
    if (!messageTemplateDetail) return null;

    const templateTypeName = messageTemplateDetail?.whatsapp_template_type?.name?.toLowerCase() ?? '';
    // if (templateTypeName !== 'basic') return null;

    const templateData = messageTemplateDetail?.whatsapp_template_data?.[0];
    if (!templateData) return null;

    const header = templateData?.whatsapp_template_header;
    const headerData = header?.whatsapp_template_header_data?.[0];
    const headerTypeName = header?.whatsapp_template_header_type_name ?? null;
    const headerTypeLower = headerTypeName?.toLowerCase() ?? '';

    const locationHeaderFields: WA_template_header_variable_data[] = [];

    let waHeaderText: string | null = null;
    let waHeaderMediaUrl: string | null = null;

    switch(headerTypeLower){
        case 'text':
            waHeaderText = replaceTemplatePlaceholders(
                headerData?.whatsapp_template_header_data_value,
                headerData?.whatsapp_template_header_variable_data,
                templateVariableData,
            );
            break;
        case 'image':
        case 'video':
        case 'document':
            const headerVariable = headerData?.whatsapp_template_header_variable_data?.[0];
            waHeaderMediaUrl =
                getTemplateVariableValue(headerVariable?.whatsapp_template_dynamic_variable_id, templateVariableData)
                || headerVariable?.whatsapp_template_dynamic_variable_sample_value
                || null;
            break;
        case 'location':
            headerData?.whatsapp_template_header_variable_data?.forEach((variable) => {
                const key = variable?.whatsapp_template_dynamic_variable_key;
                const value = getTemplateVariableValue(variable?.whatsapp_template_dynamic_variable_id, templateVariableData);
                locationHeaderFields.push({
                    action_send_wa_dm_template_header_variable_exec_data_id: "",
                    variable_value: value,
                    whatsapp_template_dynamic_variable_id: variable?.whatsapp_template_dynamic_variable_id,
                    whatsapp_template_dynamic_variable_key: key
                })
            })
            break;
        default:
            break;
    }

    const waBodyText = replaceTemplatePlaceholders(
        templateData?.whatsapp_template_body?.whatsapp_template_body_value,
        templateData?.whatsapp_template_body?.whatsapp_template_body_variable_data,
        templateVariableData,
    );

    const waButtonData = (templateData?.whatsapp_template_button || []).map((button) => {
        const buttonData = button?.whatsapp_template_button_data?.[0];
        return {
            action_send_wa_dm_template_btn_sub_type_exec_data_id: null,
            index: button?.whatsapp_template_button_index ?? null,
            wa_template_button_sub_type_id: buttonData?.whatsapp_template_button_sub_type_id
                ? Number(buttonData.whatsapp_template_button_sub_type_id)
                : null,
            wa_template_button_type_id: button?.whatsapp_template_button_type_id ?? null,
            wa_template_button_type_name: button?.whatsapp_template_button_type_name ?? null,
            wa_template_button_sub_type_name: buttonData?.whatsapp_template_button_sub_type_name ?? null,
            wa_template_button_data_id: buttonData?.whatsapp_template_button_data_id ?? null,
            wa_button_field_data: (buttonData?.whatsapp_template_button_sub_type_field_data || []).map((field) => {
                const fieldVariable = field?.whatsapp_template_button_field_data_variable?.[0];
                const dynamicValue = getTemplateVariableValue(
                    fieldVariable?.whatsapp_template_dynamic_variable_id,
                    templateVariableData,
                );
                return {
                    action_send_wa_dm_template_btn_sub_type_field_val_exec_data_id: null,
                    wa_template_button_sub_type_field_id: field?.whatsapp_template_button_sub_type_field_id ? Number(field.whatsapp_template_button_sub_type_field_id) : null,
                    wa_template_button_sub_type_field_value: field?.whatsapp_template_button_sub_type_field_value || field?.field_static_value || null,
                    field_name: field?.field_name ?? '',
                    field_type: field?.field_type ?? '',
                    wa_template_button_sub_type_field_key: field?.whatsapp_template_button_sub_type_field_payload_key || '',
                };
            }),
        };
    }) as (WA_template_action_execution_data & { wa_button_data: WA_template_action_execution_data['wa_button_data'] })['wa_button_data'];

    return {
        wa_template_id: messageTemplateDetail.whatsapp_template_id,
        wa_template_category_id: templateData?.whatsapp_template_category_id ?? null,
        wa_template_category_name: templateData?.whatsapp_template_category_name ?? null,
        wa_execution_status_id: undefined,
        wa_message_id: null,
        wa_body_text: waBodyText,
        wa_header_text: waHeaderText,
        wa_footer_text: templateData?.whatsapp_template_footer?.whatsapp_template_footer_value ?? null,
        wa_header_type_id: header?.whatsapp_template_header_type_id ?? null,
        wa_header_type_name: headerTypeName,
        wa_header_variable_data: locationHeaderFields ?? [],
        wa_header_media_chronox_storage_url: waHeaderMediaUrl,
        wa_button_data: waButtonData,
        whatsapp_template_type: messageTemplateDetail.whatsapp_template_type
            ? {
                whatsapp_template_type_id: messageTemplateDetail.whatsapp_template_type.whatsapp_template_type_id,
                name: messageTemplateDetail.whatsapp_template_type.name,
            }
            : null,
        whatsapp_message_status: [],
    };
};

/** Builds CarouselTemplatePreviewInbox-compatible action_execution_data for Carousel templates only. */
export const buildCarouselTemplateActionExecutionData = (
    messageTemplateDetail: getTemplateDetails | null,
    templateVariableData: templateVariable[],
): NonNullable<sendTemplatePayload['action_execution_data']> | null => {
    if (!messageTemplateDetail) return null;

    const templateTypeName = messageTemplateDetail?.whatsapp_template_type?.name?.toLowerCase() ?? '';
    if (templateTypeName !== 'carousel') return null;

    const sortedCards = [...(messageTemplateDetail?.whatsapp_template_data || [])]
        .sort((cardA, cardB) => Number(cardA.whatsapp_template_carousel_variation_id) - Number(cardB.whatsapp_template_carousel_variation_id));

    const waCarouselVariations = sortedCards.map((card) => {
        const header = card?.whatsapp_template_header;
        const headerData = header?.whatsapp_template_header_data?.[0];
        const headerTypeName = header?.whatsapp_template_header_type_name ?? null;
        const headerTypeLower = headerTypeName?.toLowerCase() ?? '';

        let whatsappHeaderText: string | null = null;
        let whatsappHeaderMediaUrl: string | null = null;

        if (headerTypeLower === 'text') {
            whatsappHeaderText = replaceTemplatePlaceholders(
                headerData?.whatsapp_template_header_data_value,
                headerData?.whatsapp_template_header_variable_data,
                templateVariableData,
            );
        } else if (['image', 'video', 'document'].includes(headerTypeLower)) {
            const headerVariable = headerData?.whatsapp_template_header_variable_data?.[0];
            whatsappHeaderMediaUrl =
                getTemplateVariableValue(headerVariable?.whatsapp_template_dynamic_variable_id, templateVariableData)
                || headerVariable?.whatsapp_template_dynamic_variable_sample_value
                || headerData?.whatsapp_template_header_data_value
                || null;
        }

        const whatsappBodyText = replaceTemplatePlaceholders(
            card?.whatsapp_template_body?.whatsapp_template_body_value,
            card?.whatsapp_template_body?.whatsapp_template_body_variable_data,
            templateVariableData,
        );

        const buttons = (card?.whatsapp_template_button || []).map((button) => {
            const buttonData = button?.whatsapp_template_button_data?.[0];
            return {
                action_send_wa_dm_carousel_template_btn_sub_type_exec_data_id: null,
                action_send_wa_dm_carousel_template_execution_data_variation_id: null,
                whatsapp_template_button_sub_type_id: buttonData?.whatsapp_template_button_sub_type_id ?? null,
                index: button?.whatsapp_template_button_index ?? null,
                whatsapp_template_button_type_id: button?.whatsapp_template_button_type_id ?? null,
                whatsapp_template_button_sub_type_name: buttonData?.whatsapp_template_button_sub_type_name ?? null,
                whatsapp_template_button_type_name: button?.whatsapp_template_button_type_name ?? null,
                button_fields: (buttonData?.whatsapp_template_button_sub_type_field_data || []).map((field) => {
                    const fieldVariables = field?.whatsapp_template_button_field_data_variable || [];
                    let fieldValue: string | null = null;

                    if (fieldVariables.length > 0 && typeof field.whatsapp_template_button_sub_type_field_value === 'string') {
                        fieldValue = replaceTemplatePlaceholders(
                            field.whatsapp_template_button_sub_type_field_value,
                            fieldVariables,
                            templateVariableData,
                        );
                    } else if (field?.field_static_value_flag) {
                        fieldValue = field?.field_static_value || null;
                    } else if (typeof field?.whatsapp_template_button_sub_type_field_value === 'string') {
                        fieldValue = field.whatsapp_template_button_sub_type_field_value;
                    } else {
                        fieldValue = field?.field_option_value || null;
                    }

                    return {
                        action_send_wa_dm_carousel_template_btn_sub_type_field_val_exec: null,
                        action_send_wa_dm_carousel_template_btn_sub_type_exec_data_id: null,
                        whatsapp_template_button_sub_type_field_id: field?.whatsapp_template_button_sub_type_field_id ?? null,
                        whatsapp_template_button_sub_type_field_value: fieldValue,
                        whatsapp_template_button_sub_type_field_key: field?.whatsapp_template_button_sub_type_field_payload_key || '',
                        field_name: field?.field_name ?? '',
                        field_type: field?.field_type ?? '',
                    };
                }),
            };
        });

        return {
            action_send_wa_dm_carousel_template_execution_data_variation_id: null,
            whatsapp_template_carousel_variation_id: card?.whatsapp_template_carousel_variation_id ?? null,
            whatsapp_body_text: whatsappBodyText,
            whatsapp_header_text: whatsappHeaderText,
            whatsapp_footer_text: card?.whatsapp_template_footer?.whatsapp_template_footer_value ?? null,
            whatsapp_header_media_chronox_storage_url: whatsappHeaderMediaUrl,
            whatsapp_template_header_type_id: header?.whatsapp_template_header_type_id ?? null,
            whatsapp_template_header_type_name: headerTypeName,
            buttons,
            dynamic_variables: [],
        };
    });

    const firstCard = sortedCards[0];
    const waHeaderText = replaceTemplatePlaceholders(
        messageTemplateDetail?.whtatsapp_carousel_header,
        messageTemplateDetail?.whtatsapp_carousel_header_variable_data,
        templateVariableData,
    );

    return {
        wa_template_id: messageTemplateDetail?.whatsapp_template_id ?? '',
        wa_template_category_id: firstCard?.whatsapp_template_category_id ?? null,
        wa_template_category_name: firstCard?.whatsapp_template_category_name ?? null,
        wa_execution_status_id: undefined,
        wa_message_id: null,
        wa_header_text: waHeaderText,
        wa_carousel_variations: waCarouselVariations,
        whatsapp_template_type: messageTemplateDetail.whatsapp_template_type
            ? {
                whatsapp_template_type_id: messageTemplateDetail.whatsapp_template_type.whatsapp_template_type_id,
                name: messageTemplateDetail.whatsapp_template_type.name,
            }
            : null,
        whatsapp_message_status: [],
    };
};

export const buildTemplateActionExecutionData = (
    messageTemplateDetail: getTemplateDetails | null,
    templateVariableData: templateVariable[],
): NonNullable<sendTemplatePayload['action_execution_data']> | null => {
    const carouselActionExecutionData = buildCarouselTemplateActionExecutionData(messageTemplateDetail, templateVariableData);
    if (carouselActionExecutionData) return carouselActionExecutionData;

    return buildBasicTemplateActionExecutionData(messageTemplateDetail, templateVariableData);
};

const isCarouselTemplateActionExecutionData = (
    actionExecutionData: NonNullable<sendTemplatePayload['action_execution_data']>,
): actionExecutionData is WA_carousel_template_action_execution_data => {
    return 'wa_carousel_variations' in actionExecutionData;
};

export const buildOptimisticTemplateMessages = ({
    actionExecutionData,
    userDetail,
    pendingMessageKeys,
    timestamp,
    stepTimestamp,
}: {
    actionExecutionData: NonNullable<sendTemplatePayload['action_execution_data']>;
    userDetail: UserMeResponseDataModel;
    pendingMessageKeys: string[];
    timestamp: string;
    stepTimestamp: number;
}): currentActiveThreadChatbotThreadData[] => {
    const isCarouselTemplate = isCarouselTemplateActionExecutionData(actionExecutionData);
    const actionTypeName = isCarouselTemplate ? ACTION_TYPE_NAME.CAROUSEL_TEMPLATE : ACTION_TYPE_NAME.TEMPLATE;
    const messageText = isCarouselTemplate
        ? (actionExecutionData.wa_header_text ?? '')
        : (actionExecutionData.wa_body_text ?? '');

    return [{
        thread_execution_log_id: null as unknown as string,
        chatbot_thread_id: null as unknown as string,
        chatbot_thread_value: messageText,
        timestamp,
        step_end_timestamp: String(stepTimestamp),
        step_start_timestamp: String(stepTimestamp),
        flag_user_message: false,
        flag_show_action: true,
        isPending: true,
        pendingMessageKey: pendingMessageKeys[0],
        action_type_name: actionTypeName,
        chatbot_thread_action_type: actionTypeName,
        chatbot_route_source: '',
        chatbot_user_active_flag: true,
        user_assignment_id: userDetail?.user_assignment_id ?? null,
        thread_chatbot_id: null,
        user_first_name: userDetail?.user_first_name ?? '',
        user_last_name: userDetail?.user_last_name ?? '',
        action_type_id: '',
        chatbot_step_action_id: null,
        execution_status_id: '',
        execution_status_name: '',
        group_id: null,
        parent_id: null,
        parent_thread_execution_log_id: null,
        chatbot_data: { chatbot_id: '', chatbot_name: '', chatbot_version_id: '' },
        action_execution_data: actionExecutionData as unknown as currentActiveThreadChatbotThreadData['action_execution_data'],
        chatbot_action_execution_data: {
            ...(actionExecutionData as object),
            private_user_first_name: userDetail?.user_first_name ?? null,
            private_user_last_name: userDetail?.user_last_name ?? null,
            user_assignment_id: userDetail?.user_assignment_id,
            whatsapp_message_status: actionExecutionData?.whatsapp_message_status ?? [],
        } as currentActiveThreadChatbotThreadData['chatbot_action_execution_data'],
    } as currentActiveThreadChatbotThreadData];
};

const syncThreadCache = (thread: currentActiveChatbotThreadTypeNew) => {
    const { threadCache } = useRuleBasedInboxStore.getState();
    const cachedEntry = threadCache[thread.thread_id];
    if (!cachedEntry) return;

    useRuleBasedInboxStore.setState((state) => ({
        threadCache: {
            ...state.threadCache,
            [thread.thread_id]: {
                ...cachedEntry,
                data: thread,
            },
        },
    }));
};

const updateActiveThreadMessages = (
    updater: (messages: currentActiveThreadChatbotThreadData[]) => currentActiveThreadChatbotThreadData[],
    flagRuleBasedInbox?: boolean,
) => {
    const { currentActiveChatbotThread, setCurrentActiveChatbotThread } = useRuleBasedInboxStore.getState();
    if (!currentActiveChatbotThread) return;

    const updatedMessages = sortThreadMessageByTimeStamp(
        updater(currentActiveChatbotThread.chatbot_thread_data ?? []),
    );

    const updatedThread = {
        ...currentActiveChatbotThread,
        chatbot_thread_data: updatedMessages,
    };

    setCurrentActiveChatbotThread(updatedThread);

    if (flagRuleBasedInbox) {
        syncThreadCache(updatedThread);
    }
};

export const appendPendingManualMessage = (
    optimisticMessages: currentActiveThreadChatbotThreadData[],
    flagRuleBasedInbox?: boolean,
) => {
    if (!optimisticMessages?.length) return;

    updateActiveThreadMessages(
        (messages) => [...messages, ...optimisticMessages],
        flagRuleBasedInbox,
    );

    scrollToBottomInChat();
};

export const reconcilePendingManualMessage = (
    pendingMessageKeys: string[],
    messageIds: string[],
    flagRuleBasedInbox?: boolean,
) => {
    if (!pendingMessageKeys?.length || !messageIds?.length) return;

    updateActiveThreadMessages((messages) => {
        let updatedMessages = [...messages];

        pendingMessageKeys.forEach((pendingMessageKey, index) => {
            const messageId = messageIds[index];
            if (!pendingMessageKey || !messageId) return;

            const socketAlreadyAdded = updatedMessages.some(
                (msg) => msg.thread_execution_log_id === messageId && !msg.isPending,
            );

            if (socketAlreadyAdded) {
                updatedMessages = updatedMessages.filter(
                    (msg) => !(msg.isPending && msg.pendingMessageKey === pendingMessageKey),
                );
                return;
            }

            updatedMessages = updatedMessages.map((msg) => {
                if (msg.isPending && msg.pendingMessageKey === pendingMessageKey) {
                    return {
                        ...msg,
                        thread_execution_log_id: messageId,
                        chatbot_thread_id: messageId,
                    };
                }
                return msg;
            });
        });

        return updatedMessages;
    }, flagRuleBasedInbox);
};

export const removePendingManualMessage = (
    pendingMessageKeys: string[],
    flagRuleBasedInbox?: boolean,
) => {
    if (!pendingMessageKeys?.length) return;

    const pendingKeySet = new Set(pendingMessageKeys);
    updateActiveThreadMessages(
        (messages) => messages.filter(
            (msg) => !(msg.isPending && msg.pendingMessageKey && pendingKeySet.has(msg.pendingMessageKey)),
        ),
        flagRuleBasedInbox,
    );
};
